import React, { useEffect, useState } from 'react'
import { Button, Container, Row, Col } from 'reactstrap'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { dataSlice, DataStateVersions } from '../../../../redux/slices'
import { ShowDataGrid } from '../SelectedFilesGrid/ShowDataGrid'
import { useDispatch } from 'react-redux'
import { createCheckout } from '../../../../services/endpoints/jobQueue'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { LoadIndicator } from 'devextreme-react'
import useAsyncService from '../../../../hooks/useAsyncService'

import './CheckoutSelectionModal.css'

interface SelectedFile {
  id: string
  displayName: string
  startDate: string
  endDate: string
  extension: string
  vehicle: { name: string; displayName: string }
  deviceId: number
}

interface CheckoutSelectionsModalProps {
  onClose: () => void
}

export const CheckoutSelectionModal: React.FC<CheckoutSelectionsModalProps> = ({
  onClose
}) => {
  const versions = useAppSelector(
    (state) => state.data.versions as DataStateVersions
  )
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [checkoutStarted, setCheckoutStarted] = useState(false)
  const [checkoutCompleted, setCheckoutCompleted] = useState(false)

  useEffect(() => {
    const { selectedFiles } = extractSelectedFiles(versions)
    setSelectedFiles(selectedFiles)
  }, [versions])

  const getBody = () => {
    if (!checkoutStarted) {
      return <ShowDataGrid selectedFiles={selectedFiles} />
    }

    return (
      <_CheckoutSpinner
        selectedFiles={selectedFiles}
        onCheckoutSuccess={() => setCheckoutCompleted(true)}
      />
    )
  }

  return (
    <RenderDataModal
      onClose={onClose}
      title={'Selected Files'}
      additionalButtons={
        !checkoutStarted && selectedFiles.length > 0
          ? [
              <_CheckoutButton
                key="checkout-button"
                selectedFiles={selectedFiles}
                setChecking={setCheckoutStarted}
              />,
              <_createClearAllButton
                key="clear-button"
                selectedFiles={selectedFiles}
              />
            ]
          : []
      }
    >
      <Container className="checkout-selection-container">
        <Row className="checkout-selection-row">
          <Col>{getBody()}</Col>
        </Row>
      </Container>
    </RenderDataModal>
  )
}

const _CheckoutButton: React.FC<{
  selectedFiles: SelectedFile[]
  setChecking: (value: boolean) => void
}> = ({ selectedFiles, setChecking }) => {
  const handleCheckoutSelections = () => {
    setChecking(true)
  }
  return (
    <div>
      <Button
        className="modal-btn close-btn"
        onClick={handleCheckoutSelections}
        disabled={selectedFiles.length === 0}
      >
        <span className="glyphicons glyphicons-cloud-download"></span>
        {'Checkout'}
      </Button>
    </div>
  )
}

const _createClearAllButton: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  const dispatch = useDispatch()
  const clearAllSelections = () => {
    dispatch(dataSlice.actions.clearAllSelections())
  }

  return (
    <div>
      <Button
        className="modal-btn clear-btn"
        onClick={clearAllSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-ban-circle"></span> Clear
        Selections
      </Button>
    </div>
  )
}

const extractSelectedFiles = (
  versions: DataStateVersions
): {
  selectedFiles: SelectedFile[]
} => {
  return Object.entries(versions).reduce<{
    selectedFiles: SelectedFile[]
  }>(
    (acc, [id, version]) => {
      if (version?.selected) {
        acc.selectedFiles.push({
          id,
          displayName: version.fileName,
          startDate: version.startDate,
          endDate: version.endDate,
          extension: version.type,
          deviceId: version.deviceId,
          vehicle: {
            name: version.vehicleName,
            displayName: version.vehicleDisplayName
          }
        })
      }
      return acc
    },
    { selectedFiles: [] }
  )
}

const _CheckoutSpinner: React.FC<{
  selectedFiles: SelectedFile[]
  onCheckoutSuccess: () => void
}> = ({ selectedFiles, onCheckoutSuccess }) => {
  const dispatch = useDispatch()
  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: checkoutService,
      delay: 500,
      initialArgs: null
    })

  useEffect(() => {
    if (selectedFiles.length === 0) {
      console.error('Attempting to checkout files but no files are selected')
    }
    setServiceArgs(selectedFiles)
  }, [])

  useEffect(() => {
    if (serviceValue) {
      dispatch(dataSlice.actions.clearAllSelections())
      onCheckoutSuccess()
    }
  }, [serviceValue])

  if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  }

  if (serviceLoading) {
    return <LoadIndicator />
  }

  if (serviceValue) {
    return (
      <span className={`delete-alert ${'alert-warning'}`}>
        {'All of the requested files have been marked for checkout!'}
      </span>
    )
  }

  return <></>
}

const checkoutService = async (files: SelectedFile[]) => {
  try {
    const results = await Promise.all(
      files.map((file) => createCheckout(file.id, String(file.deviceId)))
    )
    return results
  } catch (err) {
    throw new Error('One or more files failed to be marked for checkout')
  }
}

export default CheckoutSelectionModal
